import { Component, Inject, OnInit, signal } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions,
  MatDialogClose,
} from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { collection, doc, setDoc } from 'firebase/firestore';
import { dataStore, db } from 'src/app/app.component';
import { DaypartException } from 'src/app/interfaces';
import { CustomValidators } from 'angular-custom-validators';
import moment from 'moment';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatFormField, MatLabel, MatError, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import {
  MatDatepickerInput,
  MatDatepickerToggle,
  MatDatepicker,
} from '@angular/material/datepicker';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatSelect, MatOption } from '@angular/material/select';
import { MatButton } from '@angular/material/button';
import { KeyValuePipe } from '@angular/common';

export interface dialogData {
  exception?: DaypartException;
}

@Component({
  selector: 'app-manage-exception',
  templateUrl: './manage-exception.component.html',
  styleUrl: './manage-exception.component.scss',
  imports: [
    MatDialogTitle,
    CdkScrollable,
    MatDialogContent,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatError,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    MatSlideToggle,
    MatSelect,
    MatOption,
    MatDialogActions,
    MatButton,
    MatDialogClose,
    KeyValuePipe,
  ],
})
export class ManageExceptionComponent implements OnInit {
  exceptionForm: UntypedFormGroup = this.fb.group({
    title: new UntypedFormControl('', Validators.required),
    date: new UntypedFormControl(null, Validators.required),
    description: new UntypedFormControl(''),
    allDay: new UntypedFormControl(false),
    startHour: new UntypedFormControl(null, [
      Validators.min(0),
      Validators.max(23),
      CustomValidators.numberInput(false, false, 0),
    ]),
    startMinutes: new UntypedFormControl(null, [
      Validators.min(0),
      Validators.max(59),
      CustomValidators.numberInput(false, false, 0),
    ]),
    endHour: new UntypedFormControl(null, [
      Validators.min(0),
      Validators.max(23),
      CustomValidators.numberInput(false, false, 0),
    ]),
    endMinutes: new UntypedFormControl(null, [
      Validators.min(0),
      Validators.max(59),
      CustomValidators.numberInput(false, false, 0),
    ]),
    repeat: new UntypedFormControl(false),
    repeatValue: new UntypedFormControl(''),
  });
  repeatValues = {
    year: 'Jaarlijks',
    month: 'Maandelijks',
    week: 'Wekelijks',
  };
  saving = signal(false);

  constructor(
    private fb: UntypedFormBuilder,
    private dialogRef: MatDialogRef<ManageExceptionComponent>,
    private snackBar: MatSnackBar,
    @Inject(MAT_DIALOG_DATA) public data: dialogData,
  ) {}

  ngOnInit(): void {
    if (this.data.exception) {
      if (this.data.exception.repeatValue === 'never') {
        this.data.exception.repeatValue = null;
      }
      this.exceptionForm.patchValue(this.data.exception);
      this.exceptionForm.patchValue({
        date: this.data.exception.date.toDate(),
      });
      console.log(this.exceptionForm.value);
    }

    this.toggleHandler(null, true);
  }

  toggleHandler(controlName: string, init = false) {
    const controls = this.exceptionForm.controls;
    const timeControls = ['startHour', 'startMinutes', 'endHour', 'endMinutes'];

    if (controlName == 'repeat' || init) {
      if (controls.repeat.value) {
        controls.repeatValue.addValidators(Validators.required);
      } else {
        controls.repeatValue.removeValidators(Validators.required);
        controls.repeatValue.updateValueAndValidity();
      }
    }

    if (controlName == 'allDay' || init) {
      if (!this.exceptionForm.controls.allDay.value) {
        for (const control of timeControls) {
          controls[control].addValidators(Validators.required);
        }
      } else {
        for (const control of timeControls) {
          controls[control].removeValidators(Validators.required);
          controls[control].updateValueAndValidity();
        }
      }
    }
  }

  async save() {
    if (this.saving()) {
      return;
    }
    if (!this.exceptionForm.valid) {
      this.exceptionForm.markAllAsTouched();
      return;
    }
    this.saving.set(true);

    const saveObj = this.exceptionForm.value;

    if (!saveObj.repeat) {
      saveObj.repeatValue = null;
    }
    if (saveObj.allDay) {
      saveObj.startHour = null;
      saveObj.startMinutes = null;
      saveObj.endHour = null;
      saveObj.endMinutes = null;
    } else if (this.checkTimeOverlap(saveObj)) {
      this.snackBar.open('De starttijd moet voor de eindtijd plaatsvinden.', 'X', {
        duration: 5000,
      });
      this.saving.set(false);
      return;
    }

    let docRef;
    if (this.data.exception) {
      docRef = doc(
        db,
        `organisations/${dataStore.selectedOrganisation.id}/dayPartExceptions/${this.data.exception.id}`,
      );
    } else {
      docRef = doc(
        collection(db, `organisations/${dataStore.selectedOrganisation.id}/dayPartExceptions`),
      );
    }
    try {
      await setDoc(docRef, saveObj, { merge: true });
    } catch (error) {
      console.log(error);
      this.snackBar.open('Er is iets fout gegaan bij het opslaan van de uitzondering.', 'X', {
        duration: 5000,
      });
      this.saving.set(false);
      return;
    }
    this.dialogRef.close();
  }

  checkTimeOverlap(obj) {
    const saveStartTime = moment().set({
      hour: obj.startHour,
      minutes: obj.startMinutes,
    });
    const saveEndTime = moment().set({
      hour: obj.endHour,
      minutes: obj.endMinutes,
    });
    if (saveStartTime >= saveEndTime) return true;
    return false;
  }

  getError(control) {
    const field = this.exceptionForm.get(control);
    if (field.touched || !field.pristine) {
      if (field.hasError('required')) {
        return 'Dit veld is verplicht.';
      }
      if (field.hasError('min')) {
        if (control == 'startHour' || control == 'endHour') {
          return `De waarde moet 0 of hoger zijn.`;
        } else {
          return 'De waarde moet 0 of hoger zijn.';
        }
      }
      if (field.hasError('max')) {
        if (control == 'startHour' || control == 'endHour') {
          return `De waarde moet 23 of lager zijn.`;
        } else {
          return 'De waarde moet 59 of lager zijn.';
        }
      }
      if (field.hasError('numberInput')) {
        return 'De waarde mag niet decimaal zijn.';
      }
    }
    return '';
  }
}
